import { gtc, ltc } from "@base/util/Comma";
import { AdditionalArticleInfosOption, IRuleArticle, RAFF_PREMONTAGE } from "@biz/definitions/ArticleDefinitions";
import { FL } from "@biz/definitions/FL";
import { ProductVersion, Pvi } from "@biz/definitions/ProductVersion";
import { CalcProductLogic } from "@biz/items/CalcProductLogic";
import { IRaffData, RaffFieldCalculationType, RaffType } from "@biz/items/raff/RaffDefinitions";
import { RaffItemCalculator } from "@biz/items/raff/RaffItemCalculator";
import { RaffLogic } from "@biz/items/raff/RaffLogic";
import { CalcSailLogic } from "@biz/items/sunSail/CalcSailLogic";
import { LocaContext } from "@biz/language/LocaContext";
import { AdditionalArticleInfo } from "@biz/logic/AdditionalArticleInfo";
import { ClothData } from "@biz/logic/ClothData";
import { Measure } from "@biz/logic/Measure";
import { RuleArticleLogic } from "@biz/logic/RuleArticleLogic";
import { FieldErrorMap } from "@biz/models/FieldErrorMap";
import { BeosVarianten } from "@biz/orders/BeosVarianten";
import { PD } from "@biz/PD";
import { ICalcContext, ICalculatedProductResult, ProjectCheckErrorType } from "@biz/projectCalculation/ProjectDefinitions";

export class CalcRaff3dLogic {
  static calc3d(raff: IRaffData, slc: ICalcContext): ICalculatedProductResult {
    const { articleMap, errors, infos, lc } = slc;
    const raffType = raff.raffType;

    if (!articleMap || Object.keys(articleMap).length === 0)
      CalcProductLogic.addError(errors, ProjectCheckErrorType.FatalError, "product", "ArticleMap is empty");

    if (!raff.cloth) {
      CalcProductLogic.addError(errors, ProjectCheckErrorType.FatalError, "product", lc.t("Error_Cloth_Missing"));
      return {
        id: raff.id,
        pd: PD.productToPD(raff),
        infos: slc.infos,
        errors: slc.errors,
        errorMap: FieldErrorMap.errorMapFromErrors(slc.errors || []),
        articles: slc.ruleArticles,
        // modified: raff,
        product: raff
      }
    }

    CalcProductLogic.checkValidClothData(slc, PD.productToPD(raff), FL.fromProduct(raff), raff.cloth, raff.colorId);

    const clothType = raff.cloth.clothType;
    const guideInfo = RaffLogic.calcGuideInfo(raff);
    const guideCount = guideInfo.guideCount;
    const sail = RaffLogic.calcSail(raff);
    const clothSize = RaffLogic.calcClothSize(raff);
    const fieldInfo = RaffLogic.calcFieldInfo(raff);

    if (raff.overhang) {
      const minDist = 0.3;
      if (sail.size.w - (raff.overhang.left + raff.overhang.right) < minDist) {
        CalcProductLogic.addError(errors, ProjectCheckErrorType.Warning, "net", "Bei überpüfen Sie die benutzerdefinierten Auskragungen damit ein Mindestabstand von ${0}cm zwischen den Führungsschienen verbleibt...", { params: minDist * 100});
      }
    }

    if (RaffLogic.isRaffM(raffType)) {
      const distIns = RaffLogic.calcDistIns(raff);
      const minDistIn = 0.03;
      if (ltc(distIns[0], minDistIn, 2))
        CalcProductLogic.addError(errors, ProjectCheckErrorType.Warning, "AB", lc.t("Rule_Raff_DistIn_Low"), { params: minDistIn * 100})
      if (ltc(distIns[1], minDistIn, 2))
        CalcProductLogic.addError(errors, ProjectCheckErrorType.Warning, "CD", lc.t("Rule_Raff_DistIn_Low"), { params: minDistIn * 100})
    }

    const o = (RaffLogic.getRaffWidth(raff) - guideInfo.overhangLeft - guideInfo.overhangRight) / (guideCount - 1);
    if (gtc(o, RaffLogic.getGuideSettings(raff).maxGuideWidth, 2))
      CalcProductLogic.addError(errors, ProjectCheckErrorType.Warning, "BC", lc.t("Rule_Raff_Guides_Width_TooHigh"))

    const beosVariants: BeosVarianten = {}
    {
      const baseArticleId = ClothData.getBaseArticleId(raff.cloth);
      const article = articleMap[baseArticleId];
      const colorItem = RuleArticleLogic.getColorItem(article, raff.colorId);
      beosVariants["50"] = RuleArticleLogic.getRainCutVariantId(RaffLogic.getRainCut(raff), article, colorItem)
    }
    const sunSailArea = clothSize.w * clothSize.h;

    const additionalArticleInfo: AdditionalArticleInfosOption = {
      rainCut: AdditionalArticleInfo.rainCut(slc, RaffLogic.getRainCut(raff)),
      area:  AdditionalArticleInfo.areaM2(slc, sunSailArea),
      sail: { header: lc.t("ArticleInfo_Raff_SailSize"), text: Measure.toMM(sail.size.w) + "x" + Measure.toMM(sail.size.h) },
      cloth: { header: lc.t("ArticleInfo_Raff_ClothSize"), text: Measure.toMM(clothSize.w) + "x" + Measure.toMM(clothSize.h) },
      fieldCalculation: { header: lc.t("ArticleInfo_Raff_FieldCalculation"), text: lc.t(LocaContext.connect("Raff_FieldCalculation", "" + RaffFieldCalculationType[(raff.fieldCalculation?.type || RaffFieldCalculationType.Standard)])) },
      fieldCount: { header: lc.t("ArticleInfo_Raff_FieldCount"), text: "" + fieldInfo.fieldCount },
      fieldWidth: { header: lc.t("ArticleInfo_Raff_FieldWidth"), text: Measure.toMM(fieldInfo.fieldWidth) },
      fieldWidthCloth: fieldInfo.fieldWidth !== fieldInfo.fieldWidthCloth && { header: lc.t("ArticleInfo_Raff_FieldWidthCloth"), text: Measure.toMM(fieldInfo.fieldWidthCloth) },
      guideCount: { header: lc.t("ArticleInfo_Raff_GuideCount"), text: "" + guideCount },
    }

    CalcSailLogic.addClothArticle(slc, {
      cloth: raff.cloth,
      colorId: raff.colorId,
      sunSailArea,
      features: {
        colorId: raff.colorId,
        rainCut: RaffLogic.getRainCut(raff),
        raffFieldCalculationType: RaffLogic.getFieldCalculationType(raff)
      },
      beosOrder: { varianten: beosVariants },
      additionalArticleInfo
    });


    if (!ClothData.isAustronet(clothType)) {
      CalcProductLogic.addRuleArticle(slc, "00E13001011", { count: 0, articleType: "optional" }); //Soliday Cleaner Oxidationreiniger
      CalcProductLogic.addRuleArticle(slc, "00E13001010", { count: 0, articleType: "optional" }); //Soliday Cleaner Imprägniermittel
    }

    // 663 - 763
    if (RaffLogic.isPreMontagePossible(raff)) {
      if (Pvi.is(raff.version, "2024")) {
        const preMontage = RaffLogic.getPreMontage(raff);
        if (preMontage)
          CalcProductLogic.addRuleArticle(slc, preMontage, { count: 1, mul: Math.round(RaffLogic.getRaffWidth(raff) * RaffLogic.getRaffLength(raff)), articleType: "obligatory", partList: { list: "sunSailList" }, offerList: "S" }) // Vormontage Raff
      } else
        CalcProductLogic.addRuleArticle(slc, RAFF_PREMONTAGE, { count: 1, mul: Math.round(RaffLogic.getRaffWidth(raff) * RaffLogic.getRaffLength(raff)), articleType: "optional", partList: { list: "sunSailList" }, offerList: "S" }) // Vormontage Raff
    }

    if (RaffLogic.getRaffWidth(raff) < 6.001) {
      // TODO 672 DistIn
    }

    const groundSize = RaffLogic.calcGroundSize(raff);
    const distIns = RaffLogic.calcDistIns(raff);
    CalcProductLogic.addInfo(slc.infos, lc.t("Info_SailArea"), RaffLogic.calcArea(raff).toFixed(2) + Measure.m2, { group: "SunSail" })
    CalcProductLogic.addInfo(slc.infos, "Raff", Measure.toMM(RaffLogic.getRaffWidth(raff)) + " x " + Measure.toMM(RaffLogic.getRaffLength(raff)), { group: "SunSail" })
    CalcProductLogic.addInfo(slc.infos, "Raff Boden", Measure.toMM(groundSize.w) + " x " + Measure.toMM(groundSize.h), { group: "SunSail" })
    CalcProductLogic.addInfo(slc.infos, "Segel", Measure.toMM(sail.size.w) + " x " + Measure.toMM(sail.size.h), { group: "SunSail" })
    CalcProductLogic.addInfo(slc.infos, "Einzüge", Measure.toMM(distIns[0]) + " / " + Measure.toMM(distIns[1]), { group: "SunSail" })
    CalcProductLogic.addInfo(slc.infos, "Führungen", "" + guideInfo.guideCount, { group: "SunSail" })
    CalcProductLogic.addInfo(slc.infos, "Schienenabstand", Measure.toMM(guideInfo.guideWidth), { group: "SunSail" })
    CalcProductLogic.addInfo(slc.infos, "Auskragung", Measure.toMM(guideInfo.overhangLeft) + "/" + Measure.toMM(guideInfo.overhangRight), { group: "SunSail" })
    CalcProductLogic.addInfo(slc.infos, "Feldanzahl", "" + fieldInfo.fieldCount, { group: "SunSail" })
    CalcProductLogic.addInfo(slc.infos, "Feldbreite", Measure.toMM(fieldInfo.fieldWidth), { group: "SunSail" })
    CalcProductLogic.addInfo(slc.infos, "Stoffeldbreite", Measure.toMM(fieldInfo.fieldWidthCloth), { group: "SunSail" })
    CalcProductLogic.addInfo(slc.infos, "Materialzugabe", `${RaffLogic.getAdditionalMaterialFactor(raff) * 100}%`, { group: "SunSail" })
    CalcProductLogic.addInfo(slc.infos, "Stoff", Measure.toMM(clothSize.w) + " x " + Measure.toMM(clothSize.h), { group: "SunSail" })

    function addRaffMOptionalArticles(version: ProductVersion): void {
      CalcProductLogic.addRuleArticle(slc, "00E13001112", { count: 0, articleType: "optional" }) // Bedienstange Set 1,25m
      CalcProductLogic.addRuleArticle(slc, "00E13001154", { count: 0, articleType: "optional" }) // Bedienstange Set 2,00m
      CalcProductLogic.addRuleArticle(slc, "00E13001424", { count: 0, articleType: "optional" }) // Stopper - Set mit Magnet
      if (Pvi.is(version, "2024")) {
        const pulleySystem = RaffLogic.getPulleySystem(raff);
        if (pulleySystem) {
          CalcProductLogic.addRuleArticle(slc, pulleySystem, { count: 1, articleType: "obligatory", idCategory: "pulleySystem" }) // Seilzugsystem
          CalcProductLogic.addRuleArticle(slc, RaffLogic.getPulleySystemRope(raff), { offerList: "S", count: 1, mul: RaffLogic.calcPulleySystemRopeLength(raff), articleType: "obligatory", idCategory: "pulleySystem" }) // Seilzugsystem
        }
      } else {
        CalcProductLogic.addRuleArticle(slc, "00E13001371", { count: 1, articleType: "optional" }) // Seilzugsystem
      }
      CalcProductLogic.addRuleArticle(slc, "00E13001568", { count: 0, granularity: 2, articleType: "optional" }) // Seilgriffe für bessere Bedienung
      CalcProductLogic.addRuleArticle(slc, "00E13002085", { count: 0, articleType: "optional" }) // https://plaspack.atlassian.net/browse/API-65
    }

    switch (raff.raffType) {
      case RaffType.M_Rope: {
        addRaffMOptionalArticles(raff.version);
        break;
      }
      case RaffType.M_Guide: {
        addRaffMOptionalArticles(raff.version);
        CalcProductLogic.addRuleArticle(slc, "00E13001419", { count: 0, mul: RaffLogic.getRaffLength(raff), maxUnits: guideCount, articleType: "optional" }) // Winkelleiste Alu elox. inkl. Befest igungsmaterial der Führungsschiene
        break;
      }
      case RaffType.C: {
        CalcProductLogic.addRuleArticle(slc, RaffLogic.getRemoteReceiver(raff), { count: 1, }) // Seilzugsystem
        CalcProductLogic.addRuleArticle(slc, "00E13000696", { count: 1, articleType: "optional", quickCategory: "motorAdditional" }); // Hirschmannstecker(Buchse)
        CalcSailLogic.addRemoteArticles(slc, PD.productToSunSailType(raff), RaffLogic.calcRemoteChannelCount(raff));
        CalcProductLogic.addRuleArticle(slc, "00E13001321", { count: 0, articleType: "optional" }) // Soliday Reinigungs- und Pflegeset
        CalcProductLogic.addRuleArticle(slc, "00E13001419", { count: 0, mul: RaffLogic.getRaffLength(raff), maxUnits: guideCount, articleType: "optional" }) // Winkelleiste Alu elox. inkl. Befest igungsmaterial der Führungsschiene
        break;
      }
    }

    CalcProductLogic.addFlyer(slc);

    //FillRaffSubArticles 3252
    let systemArticle: IRuleArticle | undefined;
    switch (raff.raffType) {
      case RaffType.C: {
        systemArticle = CalcProductLogic.addRuleArticle(slc, "75001C", { count: 1 })
        // Zubehör Raff
        break;
      }
      case RaffType.M_Guide: {
        systemArticle = CalcProductLogic.addRuleArticle(slc, "75001M", { count: 1 })
        break;
      }
      case RaffType.M_Rope: {
        if (Pvi.is(raff, "2024"))
          systemArticle = CalcProductLogic.addRuleArticle(slc, "75002S", { count: 1 })
        else
          systemArticle = CalcProductLogic.addRuleArticle(slc, "75001S", { count: 1 })
        break;
      }
    }

    if (systemArticle) {
      RaffItemCalculator.calc(slc, raff, systemArticle);
      CalcProductLogic.calcPriceFromIncluded(systemArticle, articleMap, slc.company.surcharge, slc.priceListIndex);
    }

    CalcProductLogic.finalizeCalcContext(slc, { incomplete: !raff, defaultOfferList: "H", count: raff.count });

    return {
      id: raff.id,
      pd: PD.productToPD(raff),
      infos,
      errors,
      errorMap: FieldErrorMap.errorMapFromErrors(errors || []),
      articles: slc.ruleArticles,
      // modified: raff,
      product: raff
      // modifiedSunSail: sunSail,
    };
  }
}